import { useCallback, useEffect, useRef, useState } from "react";
import {
  OTP_EXPIRY_MINUTES,
  OTP_LENGTH,
  OTP_MAX_ATTEMPTS,
  OTP_RESEND_COOLDOWN_SECONDS,
  generateOtp,
  sendOtpEmail
} from "../api/utils/otp";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (email) => EMAIL_PATTERN.test(email?.trim() ?? "");

const INITIAL_STATE = {
  otp: "",
  otpSent: false,
  emailVerified: false,
  sendingOtp: false,
  verifyingOtp: false,
  attemptsLeft: OTP_MAX_ATTEMPTS
};

const NO_COOLDOWN = { active: false, secondsLeft: 0 };

/**
 * Drives the email-OTP gate on the signup form.
 *
 * `email` is the live value of the email field - changing it invalidates any code
 * already issued, so a user cannot verify one address and then submit another.
 *
 * `notify({ severity, msg })` is called for every user-facing outcome so the caller
 * can route it into whatever snackbar it already renders.
 */
export function useEmailOtp({ email, notify = () => {} }) {
  const [state, setState] = useState(INITIAL_STATE);
  const [cooldown, setCooldown] = useState(NO_COOLDOWN);

  // kept in refs so the code and its deadlines never land in the render tree
  const issuedOtpRef = useRef(null);
  const expiresAtRef = useRef(0);
  const resendAtRef = useRef(0);

  const patch = useCallback((next) => setState((prev) => ({ ...prev, ...next })), []);

  const reset = useCallback(() => {
    issuedOtpRef.current = null;
    expiresAtRef.current = 0;
    resendAtRef.current = 0;
    setState(INITIAL_STATE);
    setCooldown(NO_COOLDOWN);
  }, []);

  // Editing the email throws away the previous verification.
  useEffect(() => {
    reset();
  }, [email, reset]);

  // Resend cooldown, so a user cannot hammer the mail proxy. The remaining time is
  // recomputed from a deadline rather than decremented, so a single interval covers
  // the whole countdown and a throttled background tab cannot make it drift.
  useEffect(() => {
    if (!cooldown.active) return undefined;

    const id = setInterval(() => {
      const secondsLeft = Math.max(
        0,
        Math.ceil((resendAtRef.current - Date.now()) / 1000)
      );

      setCooldown(secondsLeft > 0 ? { active: true, secondsLeft } : NO_COOLDOWN);
    }, 1000);

    return () => clearInterval(id);
  }, [cooldown.active]);

  const sendOtp = useCallback(async () => {
    const trimmed = email?.trim();

    if (!isValidEmail(trimmed)) {
      notify({ severity: "warning", msg: "Please enter a valid email address first" });

      return false;
    }

    patch({ sendingOtp: true });

    try {
      const otp = generateOtp();

      await sendOtpEmail({ email: trimmed, otp });

      issuedOtpRef.current = otp;
      expiresAtRef.current = Date.now() + OTP_EXPIRY_MINUTES * 60 * 1000;
      resendAtRef.current = Date.now() + OTP_RESEND_COOLDOWN_SECONDS * 1000;

      patch({ otp: "", otpSent: true, attemptsLeft: OTP_MAX_ATTEMPTS });
      setCooldown({ active: true, secondsLeft: OTP_RESEND_COOLDOWN_SECONDS });

      // The address is deliberately not interpolated here - the signup snackbar
      // renders its message with dangerouslySetInnerHTML, so field input must not
      // reach it. The form shows the address next to the OTP input instead.
      notify({
        severity: "success",
        msg: `We've sent a ${OTP_LENGTH} digit code to your email address. It is valid for ${OTP_EXPIRY_MINUTES} minutes.`
      });

      return true;
    } catch (error) {
      console.error("Failed to send the OTP", error);

      notify({
        severity: "error",
        msg: "We couldn't send the verification code. Please try again."
      });

      return false;
    } finally {
      patch({ sendingOtp: false });
    }
  }, [email, notify, patch]);

  const verifyOtp = useCallback(async () => {
    const entered = state.otp?.trim();

    if (!issuedOtpRef.current) {
      notify({ severity: "warning", msg: "Please request a verification code first" });

      return false;
    }

    if (entered?.length !== OTP_LENGTH) {
      notify({
        severity: "warning",
        msg: `Please enter the ${OTP_LENGTH} digit code we emailed you`
      });

      return false;
    }

    patch({ verifyingOtp: true });

    try {
      if (Date.now() > expiresAtRef.current) {
        issuedOtpRef.current = null;

        patch({ otp: "", otpSent: false });

        notify({
          severity: "error",
          msg: "That code has expired. Please request a new one."
        });

        return false;
      }

      if (entered !== issuedOtpRef.current) {
        const attemptsLeft = state.attemptsLeft - 1;

        if (attemptsLeft <= 0) {
          // burn the code so a fresh one has to be requested
          issuedOtpRef.current = null;

          patch({ otp: "", otpSent: false, attemptsLeft: OTP_MAX_ATTEMPTS });

          notify({
            severity: "error",
            msg: "Too many incorrect attempts. Please request a new code."
          });

          return false;
        }

        patch({ attemptsLeft });

        notify({
          severity: "error",
          msg: `Incorrect code. ${attemptsLeft} attempt${
            attemptsLeft === 1 ? "" : "s"
          } left.`
        });

        return false;
      }

      issuedOtpRef.current = null;
      resendAtRef.current = 0;

      patch({ emailVerified: true });
      setCooldown(NO_COOLDOWN);

      notify({ severity: "success", msg: "Email verified successfully" });

      return true;
    } finally {
      patch({ verifyingOtp: false });
    }
  }, [notify, patch, state.attemptsLeft, state.otp]);

  const setOtp = useCallback(
    (value) => {
      // the field is numeric-only, so drop anything a paste might bring along
      patch({ otp: value.replace(/\D/g, "").slice(0, OTP_LENGTH) });
    },
    [patch]
  );

  // Lets an OAuth signup mark the provider-supplied email as already trusted.
  const markVerified = useCallback(() => {
    issuedOtpRef.current = null;
    resendAtRef.current = 0;

    patch({ emailVerified: true, otpSent: false, otp: "" });
    setCooldown(NO_COOLDOWN);
  }, [patch]);

  return {
    ...state,
    resendIn: cooldown.secondsLeft,
    otpLength: OTP_LENGTH,
    sendOtp,
    verifyOtp,
    setOtp,
    markVerified,
    reset
  };
}
